/**
 * Supabase Client Configuration
 *
 * Uses a custom fetch wrapper with a configurable timeout so auth (e.g. Google
 * signInWithIdToken) and large admin queries have time to complete; default is 60s.
 * Set VITE_SUPABASE_FETCH_TIMEOUT_MS to override. If the project is paused or
 * unreachable, restore it in the Supabase dashboard.
 *
 * If you see ERR_QUIC_PROTOCOL_ERROR in Chrome: try disabling QUIC
 * (chrome://flags → "Experimental QUIC protocol" → Disabled) or use another browser.
 *
 * **Env contract:** `VITE_SUPABASE_URL` must be `https://<ref>.supabase.co` (the project URL).
 * Some Netlify configs mistakenly set it to the gograbjob HTTP proxy; we then infer
 * `https://<ref>.supabase.co` from the public anon JWT’s `ref` claim, or you can set
 * `VITE_SUPABASE_PROJECT_URL` explicitly. The gograbjob path belongs only in
 * `VITE_SUPABASE_PROXY_URL` and is used when the app and proxy share a hostname (www only).
 */

import { Capacitor } from '@capacitor/core';
import { createClient } from '@supabase/supabase-js';

const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'your-anon-key';

/** The hosted REST API is always on https://<ref>.supabase.co — never a marketing or proxy domain. */
function isValidSupabaseProjectUrl(url: string): boolean {
  try {
    const { hostname } = new URL(url);
    return /^[a-z0-9-]{10,30}\.supabase\.co$/i.test(hostname);
  } catch {
    return false;
  }
}

/** Public anon key JWT includes `ref` = Supabase project ref (same for all public clients). */
function inferProjectUrlFromAnonKey(anonKey: string): string | null {
  if (!anonKey || anonKey === 'your-anon-key') return null;
  try {
    const parts = anonKey.split('.');
    if (parts.length < 2) return null;
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
    const json = JSON.parse(atob(b64 + pad)) as { ref?: string };
    if (typeof json.ref === 'string' && json.ref.length > 0) {
      return `https://${json.ref}.supabase.co`;
    }
  } catch {
    return null;
  }
  return null;
}

function resolveSupabaseProjectUrl(anonKey: string): string {
  const projectOverride = import.meta.env.VITE_SUPABASE_PROJECT_URL?.trim();
  if (projectOverride) return projectOverride;

  const fromEnv = import.meta.env.VITE_SUPABASE_URL?.trim() || 'https://your-project.supabase.co';
  if (isValidSupabaseProjectUrl(fromEnv)) {
    return fromEnv;
  }

  // Any non-*.supabase.co (gograbjob proxy, typo, custom domain) — infer from the public key.
  const inferred = inferProjectUrlFromAnonKey(anonKey);
  if (inferred) {
    if (import.meta.env.DEV) {
      console.info(
        '[supabase] VITE_SUPABASE_URL is not a project URL (*.supabase.co); using https://<ref>.supabase.co from the anon key. Set VITE_SUPABASE_URL in Netlify to that value.'
      );
    }
    return inferred;
  }

  console.error(
    '[supabase] VITE_SUPABASE_URL is not a valid project URL; could not infer from anon key. Set VITE_SUPABASE_URL or VITE_SUPABASE_PROJECT_URL to https://<ref>.supabase.co'
  );
  return fromEnv;
}

// Direct project URL (always *.supabase.co after resolve). Realtime uses this; HTTP may rewrite to proxy on www.
const directUrl = resolveSupabaseProjectUrl(supabaseAnonKey);

// Optional: same-origin HTTP proxy (ISP workaround on production host only).
const proxyUrl = Capacitor.isNativePlatform()
  ? ''
  : (import.meta.env.VITE_SUPABASE_PROXY_URL || '');

// Configurable timeout: auth and large admin queries (outreach, analytics) need headroom when
// the browser connection pool is saturated or the project is cold. Default 60s; override with
// VITE_SUPABASE_FETCH_TIMEOUT_MS.
const FETCH_TIMEOUT_MS =
  Number(import.meta.env.VITE_SUPABASE_FETCH_TIMEOUT_MS) || 60_000;

const TIMEOUT_MESSAGE = `Supabase request timed out after ${FETCH_TIMEOUT_MS / 1000}s. Check network or restore the project in Supabase dashboard.`;

/**
 * The HTTP proxy (gograbjob.com/supabase-api) is only same-origin when the app is
 * also served from that host. If we rewrote fetches to the proxy from *.netlify.app,
 * localhost, or another host, the browser would cross-origin preflight to gograbjob.com
 * and the proxy CORS may omit PostgREST headers (e.g. `Prefer`, `accept-profile`).
 */
function shouldRewriteToProxy(): boolean {
  if (!proxyUrl) return false;
  if (typeof window === 'undefined') return false;
  try {
    return new URL(proxyUrl).hostname === window.location.hostname;
  } catch {
    return false;
  }
}

// HTTP: rewrite to site proxy only when same-origin. Otherwise requests stay on directUrl (Supabase CORS).
function fetchWithProxy(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => {
    controller.abort(new DOMException(TIMEOUT_MESSAGE, 'AbortError'));
  }, FETCH_TIMEOUT_MS);

  const callerSignal = init?.signal;
  const onCallerAbort = () => {
    controller.abort(callerSignal?.reason ?? new DOMException('Aborted', 'AbortError'));
  };
  if (callerSignal) {
    if (callerSignal.aborted) onCallerAbort();
    else callerSignal.addEventListener('abort', onCallerAbort, { once: true });
  }

  const raw = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url;

  // Off-site preview (e.g. *.netlify.app): if anything still requests the gograbjob proxy, force
  // the real project host. Handles stale bundles, env mistakes, and createClient() edge cases.
  if (
    proxyUrl &&
    !shouldRewriteToProxy() &&
    isValidSupabaseProjectUrl(directUrl) &&
    raw.startsWith(proxyUrl)
  ) {
    const unproxied = raw.replace(proxyUrl, directUrl);
    input = typeof input === 'string' ? unproxied : new URL(unproxied);
  } else if (proxyUrl && shouldRewriteToProxy() && raw.startsWith(directUrl)) {
    const rewritten = raw.replace(directUrl, proxyUrl);
    input = typeof input === 'string' ? rewritten : new URL(rewritten);
  }

  return fetch(input, { ...init, signal: controller.signal }).finally(() => {
    clearTimeout(timer);
    callerSignal?.removeEventListener('abort', onCallerAbort);
  });
}

export const supabase = createClient(directUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
  global: {
    fetch: fetchWithProxy,
  },
});

export default supabase;
